// @catalog F.047 F.048
// Bloque "Importar" de /mercado-libre: subir un export oficial (Mercado
// Libre, Amazon o Walmart — el backend detecta la plataforma por headers) y
// tabla de batches recientes con estado vivo (polling React Query hasta que
// ningún batch siga en "procesando"). Errores y hojas rechazadas visibles —
// prohibida la importación parcial silenciosa (spec restricción 4).
import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BodyCell,
  BodyRow,
  HeaderCell,
  TableShell,
} from "../analytics/TableCells";
import MercadoLibreImportService, {
  type ImportBatch,
  type Marketplace,
} from "../../services/mercadoLibreImport.service";

const POLL_MS = 2500;

const STATUS_CHIP: Record<
  ImportBatch["status"],
  { label: string; color: "default" | "success" | "error" | "warning" }
> = {
  procesando: { label: "Procesando…", color: "warning" },
  completado: { label: "Completado", color: "success" },
  error: { label: "Error", color: "error" },
};

const MARKETPLACE_LABEL: Record<Marketplace, string> = {
  mercado_libre: "Mercado Libre",
  amazon: "Amazon",
  walmart: "Walmart",
};

const fmtFecha = (iso: string): string => iso.replace("T", " ").slice(0, 19);

export function ImportacionMlBlock(): JSX.Element {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const batchesQuery = useQuery({
    queryKey: ["ml-import-batches"],
    queryFn: () => MercadoLibreImportService.listImports(20),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((b) => b.status === "procesando")
        ? POLL_MS
        : false,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => MercadoLibreImportService.uploadImport(file),
    onSuccess: () => {
      setUploadError(null);
      void queryClient.invalidateQueries({ queryKey: ["ml-import-batches"] });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo subir el archivo";
      setUploadError(String(detail));
    },
  });

  const handleFileChosen = (files: FileList | null): void => {
    const file = files?.[0];
    if (!file) return;
    uploadMutation.mutate(file);
    // permite volver a subir el mismo archivo (demostración del dedup, CR-1)
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const batches = batchesQuery.data ?? [];

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
        <Typography
          component="h2"
          sx={{ fontSize: "16px", fontWeight: 600, color: "var(--ink-1)" }}
        >
          Importar export de ecommerce (Mercado Libre · Amazon · Walmart)
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          size="small"
          startIcon={
            uploadMutation.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <UploadFileIcon />
            )
          }
          disabled={uploadMutation.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploadMutation.isPending ? "Subiendo…" : "Subir .xlsx"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          hidden
          onChange={(e) => handleFileChosen(e.target.files)}
        />
      </Stack>

      {uploadError && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      <TableShell maxHeight="max(340px, calc(100vh - 300px))">
        <Box component="thead">
          <Box component="tr">
            <HeaderCell>Archivo</HeaderCell>
            <HeaderCell>Plataforma</HeaderCell>
            <HeaderCell>Subido</HeaderCell>
            <HeaderCell>Estado</HeaderCell>
            <HeaderCell align="right">Nuevas</HeaderCell>
            <HeaderCell align="right">Actualizadas</HeaderCell>
            <HeaderCell align="right">Sin cambio</HeaderCell>
            <HeaderCell align="right">Hojas</HeaderCell>
            <HeaderCell align="right">Match Odoo</HeaderCell>
            <HeaderCell>Detalle</HeaderCell>
          </Box>
        </Box>
        <Box component="tbody">
          {batches.length === 0 && (
            <Box component="tr">
              <BodyCell muted>
                {batchesQuery.isLoading
                  ? "Cargando…"
                  : "Sin importaciones todavía — sube el primer export."}
              </BodyCell>
              <BodyCell>{""}</BodyCell>
              <BodyCell>{""}</BodyCell>
              <BodyCell>{""}</BodyCell>
              <BodyCell>{""}</BodyCell>
              <BodyCell>{""}</BodyCell>
              <BodyCell>{""}</BodyCell>
              <BodyCell>{""}</BodyCell>
              <BodyCell>{""}</BodyCell>
              <BodyCell>{""}</BodyCell>
            </Box>
          )}
          {batches.map((b) => {
            const chip = STATUS_CHIP[b.status];
            const matchTotal = b.rowsMatched + b.rowsUnmatched;
            return (
              <BodyRow key={b.id}>
                <BodyCell>
                  <Tooltip title={`SHA-256 ${b.fileHash.slice(0, 16)}…`} arrow>
                    <span>{b.fileName}</span>
                  </Tooltip>
                </BodyCell>
                <BodyCell muted>
                  {b.marketplace ? MARKETPLACE_LABEL[b.marketplace] : "—"}
                </BodyCell>
                <BodyCell muted>{fmtFecha(b.createdAt)}</BodyCell>
                <BodyCell>
                  <Chip size="small" label={chip.label} color={chip.color} />
                </BodyCell>
                <BodyCell align="right">{b.rowsNew.toLocaleString()}</BodyCell>
                <BodyCell align="right">
                  {b.rowsUpdated.toLocaleString()}
                </BodyCell>
                <BodyCell align="right">
                  {b.rowsUnchanged.toLocaleString()}
                </BodyCell>
                <BodyCell align="right">{b.sheetsProcessed}</BodyCell>
                <BodyCell align="right">
                  {matchTotal > 0
                    ? `${b.rowsMatched.toLocaleString()} / ${matchTotal.toLocaleString()}`
                    : "—"}
                </BodyCell>
                <BodyCell muted>
                  {b.status === "error" && (b.errorDetail ?? "Error")}
                  {b.status !== "error" &&
                    b.sheetsRejected.length > 0 && (
                      <Tooltip
                        arrow
                        title={b.sheetsRejected
                          .map((s) => `${s.sheet}: ${s.reason}`)
                          .join(" · ")}
                      >
                        <span>{b.sheetsRejected.length} hoja(s) rechazada(s)</span>
                      </Tooltip>
                    )}
                  {b.status === "completado" &&
                    b.sheetsRejected.length === 0 &&
                    "OK"}
                </BodyCell>
              </BodyRow>
            );
          })}
        </Box>
      </TableShell>
    </Box>
  );
}
